import Notifications from '../../services/notifications';
import { FrequencySubscriptionTypes } from '../../services/utilityFunctions';

/**
 * Compose the url to fetch all products (pack or with variations) in cart
 * @param cartToFetch
 * @returns {{endpoint: string, variations: *, products: *}}
 */
const getCheckoutProductEndpoint = cartToFetch => {
  let endpoint = '/api/market/product/list?';
  // Get normal products
  const variations = cartToFetch.filter(c => !c.isPack).map(c => c.id);
  // Get pack products
  const products = cartToFetch.filter(c => c.isPack).map(c => c.id);

  if (variations.length > 0) {
    // add filter to get parent product of variations in cart
    endpoint += variations.map(v => `variations[]=${v}`).join('&');
  }
  if (products.length) {
    if (variations.length) {
      endpoint += '&';
    }
    // add filter to get pack products in cart
    endpoint += products.map(p => `products[]=${p}`).join('&');
  }

  return {
    endpoint,
    variations,
    products,
  };
};

const handleFrequencyByProduct = product => {
  const {
    availableForSubscription = false,
    availableSubscriptionFrequency = [],
  } = product;
  let frequency = FrequencySubscriptionTypes.ONLY_ONCE;

  if (availableForSubscription && availableSubscriptionFrequency?.length > 0) {
    const hasOnlyOnce = availableSubscriptionFrequency.find(
      f => f === FrequencySubscriptionTypes.ONLY_ONCE,
    );
    if (!hasOnlyOnce) {
      [frequency] = availableSubscriptionFrequency;
    }
  }
  return frequency;
};

/**
 * Compose the product list display in checkout
 * @param t
 * @param isCart
 * @param variations
 * @param products
 * @param cartToFetch
 * @param productsResult
 */
const getCheckoutProductList = (
  t,
  isCart,
  variations,
  products,
  cartToFetch,
  productsResult,
) => {
  // region Find cart variations in parent product
  const productVariations = variations
    .map(variationId => {
      const product = productsResult.find(parentProduct =>
        parentProduct.variations.find(
          variationProduct => variationProduct._id === variationId,
        ),
      );
      if (!product) {
        return null;
      }
      return product.variations.find(v => v._id === variationId);
    })
    .filter(p => p);
  // endregion Find cart variations in parent product
  // region Find pack product
  const packProducts = products
    .map(productId => {
      const product = productsResult.find(
        parentProduct => parentProduct._id === productId,
      );
      if (!product) {
        return null;
      }
      return product;
    })
    .filter(p => p);
  // endregion Find pack product

  // region Compare to check if all products are available
  if (
    [...variations, ...products].length >
    [...productVariations, ...packProducts].length
  ) {
    Notifications.showNotificationWarning(
      t('warning'),
      t('shop.checkout.availability.products'),
    );
  }
  // endregion Compare to check if all products are available

  // region show the actual cart after every match
  const newCart = cartToFetch.map(cartItem => {
    const { id: cartItemId, isPack } = cartItem;
    let product;
    // manage normal products
    if (!isPack) {
      product = productsResult.find(parentProduct =>
        parentProduct.variations.find(
          productVariation => productVariation._id === cartItemId,
        ),
      );
    } else {
      // manage pack products
      product = productsResult.find(
        parentProduct => parentProduct._id === cartItemId,
      );
    }
    if (!product) {
      return null;
    }

    // manage the pack information in a similar way to the variations
    const productItem = !isPack
      ? product.variations.find(v => v._id === cartItemId)
      : {
          ...product,
          ...product?.pack,
          icon: product?.images[0],
        };
    return {
      ...cartItem,
      name: productItem.name,
      image: productItem.icon?.thumb,
    };
  });
  // endregion show the actual cart after every match
  return isCart
    ? newCart.filter(p => p !== null)
    : newCart
        .filter(p => p !== null)
        .map(cartProduct => {
          const {
            isPack,
            id: cartItemId,
            amount,
            packVariations: advancedPackVariations,
            isAdvancedPack = false,
            amountVariationsPerAdvancedPack,
          } = cartProduct || {};
          if (isPack) {
            const product = productsResult.find(p => p._id === cartItemId);
            if (!product) return null;
            // It is used to identify which products are digital
            const isDigitalContent = product.isDigital;
            const { pack, packVariations } = product;
            return {
              name: product.name,
              variationName: t(
                'shop.pack.quantity.items',
                isAdvancedPack
                  ? amountVariationsPerAdvancedPack
                  : packVariations?.length,
              ),
              packVariations: isAdvancedPack
                ? advancedPackVariations
                : undefined,
              isAdvancedPack,
              stockQuantity: null,
              restockDate: null,
              restockInterval: null,
              lowStockWarning: null,
              amount,
              images: product.images,
              id: cartItemId,
              productId: product._id,
              category: product.category,
              va: pack.va,
              cv: pack.cv,
              commissionable: pack.commissionable,
              availableCountries: product.availableCountries,
              isDigitalContent,
              isPack: true,
              availableSubscriptionFrequency:
                cartProduct.availableSubscriptionFrequency ||
                product.availableSubscriptionFrequency,
              availableForSubscription:
                cartProduct.availableForSubscription ||
                product.availableForSubscription,
              frequency:
                cartProduct.subscriptionType ||
                handleFrequencyByProduct(product),
            };
          }
          const product = productsResult.find(parentProduct =>
            parentProduct.variations.find(
              productVariation => productVariation._id === cartItemId,
            ),
          );

          if (!product) return null;

          const variation = product.variations.find(
            productVariation => productVariation._id === cartItemId,
          );
          // It is used to identify which products are digital
          const isDigitalContent = product.isDigital
            ? product?.isDigital
            : variation?.digitalDocument &&
              variation?.digitalDocument?.url?.length > 0;

          return {
            name: product.name,
            variationName: variation.name,
            stockQuantity: variation.stockQuantity,
            restockDate: variation.restockDate,
            restockInterval: variation.restockInterval,
            lowStockWarning: variation.lowStockWarning,
            amount,
            images: product.images,
            id: cartItemId,
            productId: product._id,
            category: product.category,
            va: variation.va,
            cv: variation.cv,
            commissionable: variation.commissionable,
            availableCountries: product.availableCountries,
            isDigitalContent,
            isPack: false,
            availableSubscriptionFrequency:
              cartProduct.availableSubscriptionFrequency ||
              product.availableSubscriptionFrequency,
            availableForSubscription:
              cartProduct.availableForSubscription ||
              product.availableForSubscription,
            frequency:
              cartProduct.subscriptionType || handleFrequencyByProduct(product),
          };
        })
        .filter(p => p !== null);
};

const getProductsForOrder = (cartProducts, isForCouponsAvailability = false) =>
  cartProducts?.map(p => {
    const { isPack, isAdvancedPack, packVariations } = p;

    // Normal Pack
    if (isPack && !isAdvancedPack) {
      return {
        packProduct: p.id,
        quantity: p.amount,
        type: 'pack',
      };
    }

    // Advanced Pack
    if (isPack && isAdvancedPack) {
      return {
        packProduct: p.id,
        quantity: p.amount,
        type: 'pack_advanced',
        packVariations: packVariations?.map(variation => ({
          variation: variation._id || variation?.variation,
          quantity: variation.quantity,
          stockQuantity: variation.stockQuantity,
        })),
      };
    }

    // Normal product
    return {
      [isForCouponsAvailability ? 'variation' : 'product']: p.id,
      quantity: p.amount,
      type: 'product',
    };
  });

export {
  getCheckoutProductEndpoint,
  getCheckoutProductList,
  getProductsForOrder,
};
